import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { OrganizationCustomerDTO } from '../../../../dto/organization/organization-customer.dto';
import {
  formatCustomerAddress,
  normalizeSpreadsheetNumericString,
} from '../../../../utils/customer-csv.util';

function normalizeCustomerRow(
  row: OrganizationCustomerDTO
): OrganizationCustomerDTO {
  return {
    ...row,
    ean: normalizeSpreadsheetNumericString(row.ean ?? undefined) ?? row.ean,
    cvr: normalizeSpreadsheetNumericString(row.cvr ?? undefined) ?? row.cvr,
  };
}

export function duna_customers(componentDataSourceService: ComponentDataSourceService) {
  return {
    name: 'DUNA Kunder',
    description: 'Liste over kunder tilknyttet organisationen',
    execute: (params?: LoadDataParams): Observable<any> => {
      return componentDataSourceService['loadDataWithStandardFiltersGET'](
        'duna_customers',
        '/organizations/customers',
        params ?? ({} as any)
      ).pipe(
        catchError(error => {
          console.error('Error fetching customers:', error);
          return of({ items: [], totalCount: 0, pageSize: 10, pageIndex: 0 });
        }),
        map(result => {
          const items = ((result?.items || []) as OrganizationCustomerDTO[]).map(
            normalizeCustomerRow
          );
          return {
            items,
            totalCount: result?.totalCount ?? items.length,
            pageSize: result?.pageSize ?? 10,
            pageIndex: result?.pageIndex ?? 0,
          };
        })
      );
    },
    settings: {
      columns: [
        {
          key: 'name',
          header: 'Navn',
          type: 'text',
          sortable: true,
          wrap: true,
          fill: true,
        },
        { key: 'att', header: 'Att.', type: 'text', sortable: true, wrap: true },
        { key: 'email', header: 'E-mail', type: 'text', sortable: true, wrap: true },
        { key: 'phone', header: 'Telefon', type: 'text', sortable: true },
        { key: 'mobile', header: 'Mobil', type: 'text', sortable: true },
        {
          key: 'address',
          header: 'Adresse',
          type: 'text',
          sortable: true,
          wrap: true,
          fill: true,
          transform: (_value: unknown, row?: OrganizationCustomerDTO) =>
            row ? formatCustomerAddress(row) : '—',
        },
        {
          key: 'price_group_label',
          header: 'Prisgruppe',
          type: 'text',
          sortable: true,
          wrap: true,
          transform: (value: unknown) => {
            const label = String(value ?? '').trim();
            return label || '—';
          },
        },
        {
          key: 'ean',
          header: 'EAN',
          type: 'text',
          sortable: true,
          transform: (value: unknown) =>
            normalizeSpreadsheetNumericString(
              value != null ? String(value) : undefined
            ) ?? '—',
        },
        {
          key: 'cvr',
          header: 'CVR',
          type: 'text',
          sortable: true,
          transform: (value: unknown) =>
            normalizeSpreadsheetNumericString(
              value != null ? String(value) : undefined
            ) ?? '—',
        },
        {
          key: 'view_events',
          header: '',
          type: 'icon-button',
          icon: 'event_note',
          buttonText: 'Se events',
          buttonColor: 'primary',
          alignment: 'center',
          fitContent: true,
          width: '1%',
          maxWidth: '52px',
          action: (row: OrganizationCustomerDTO) => {
            window.dispatchEvent(
              new CustomEvent('duna-customers-view-events', { detail: row })
            );
          },
        },
        {
          key: 'edit_customer',
          header: '',
          type: 'icon-button',
          icon: 'edit',
          buttonText: 'Rediger',
          buttonColor: 'primary',
          alignment: 'center',
          fitContent: true,
          width: '1%',
          maxWidth: '52px',
          action: (row: OrganizationCustomerDTO) => {
            window.dispatchEvent(
              new CustomEvent('duna-customers-edit', { detail: row })
            );
          },
        },
        {
          key: 'delete_customer',
          header: '',
          type: 'icon-button',
          icon: 'delete_outline',
          buttonText: 'Deaktiver',
          buttonColor: 'warn',
          alignment: 'center',
          fitContent: true,
          width: '1%',
          maxWidth: '52px',
          action: (row: OrganizationCustomerDTO) => {
            window.dispatchEvent(
              new CustomEvent('duna-customers-delete', { detail: row })
            );
          },
        },
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      searchPlaceholder: 'Søg i alle felter (regex: /mønster/)',
      tableLayout: 'auto',
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc',
    },
  };
}
